import { Link } from '@tanstack/react-router'
import type { ComponentProps, ReactNode } from 'react'
import { useLocalizedHref } from '@/hooks/useLocalizedHref'

/**
 * Drop-in replacement for TanStack <Link> that prefixes the href with /he or
 * /de when the target is a prerendered path AND the runtime language is non-en.
 *
 * Use this for any internal link to a prerendered route (homepage + the 8 content
 * pages). Non-prerendered targets pass through unchanged, so it's safe to use
 * generically when the target type isn't known at the call site.
 *
 * Note on typing: TanStack's <Link to> is a strict literal union of registered
 * routes, but our localized href is computed at runtime. We accept an anchor-
 * like prop set and cast through `any` so callers can pass plain `to="/foo"`.
 * Runtime behavior is correct — TanStack accepts arbitrary string paths.
 */
type AnchorProps = Omit<ComponentProps<'a'>, 'href'>

export interface LocalizedLinkProps extends AnchorProps {
  to: string
  hash?: string
  children?: ReactNode
}

export function LocalizedLink({ to, children, ...rest }: LocalizedLinkProps) {
  const localized = useLocalizedHref(to)
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Link to={localized as any} {...(rest as any)}>
      {children}
    </Link>
  )
}
